// oxlint-disable-next-line typescript/triple-slash-reference -- an ambient wildcard module can't be imported.
/// <reference path="../inline-css.d.ts" />
/**
 * A component the agent built, in frames that can only show things. Page text can steer the model, so its code must have
 * no way to send anything anywhere.
 *
 * Its frame has an opaque origin (no `allow-same-origin`), so no cookies, storage or page. A CSP stops it loading anything,
 * but a CSP does not stop a frame navigating itself to an address carrying data, so it sits inside a second frame whose CSP
 * only allows `about:` frames. It also can't be clicked or typed into, so a fake sign-in form inside it has nothing to read.
 */
import { useMemo } from 'react'
import TOKENS from '../styles/tokens.css?inline'

const CSP =
  "default-src 'none'; script-src 'unsafe-inline'; style-src 'unsafe-inline'; img-src data:; font-src data:; frame-src about:; base-uri 'none'; form-action 'none'; webrtc 'block'"

const META = `<meta http-equiv="Content-Security-Policy" content="${CSP}">`

/** Props go inside a <script>, so a value can't close it. */
const inScript = (value: unknown) => JSON.stringify(value).replace(/</g, '\\u003c')

const inAttribute = (value: string) => value.replace(/&/g, '&amp;').replace(/"/g, '&quot;')

export function CustomFrame({ html, props, height }: { html: string; props: Record<string, unknown>; height: number }) {
  const doc = useMemo(() => {
    const inner = `<!doctype html><html><head><meta charset="utf-8">${META}<style>${TOKENS}html,body{margin:0;background:transparent;color:var(--ink);font:400 var(--text-body)/1.45 var(--font-sans);}</style><script>window.softmax=Object.freeze({props:${inScript(props)}});</script></head><body>${html}</body></html>`
    return `<!doctype html><html><head><meta charset="utf-8">${META}<style>html,body,iframe{border:0;display:block;height:100%;margin:0;width:100%;}</style></head><body><iframe sandbox="allow-scripts" srcdoc="${inAttribute(inner)}"></iframe></body></html>`
  }, [html, props])
  return (
    <figure className="sc-custom">
      <iframe
        className="sc-custom-frame"
        title="Made by your cogent"
        sandbox="allow-scripts"
        srcDoc={doc}
        style={{ height }}
        tabIndex={-1}
        inert
      />
      <figcaption className="sc-custom-note">Made by your cogent</figcaption>
    </figure>
  )
}
